import { useMutation, useQueryClient } from 'react-query'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { UseMutationOptions } from 'react-query/types/react/types'
import { sendToApi } from 'api/client'
import { BrandKit, ExampleBrand, PlainText, Website } from 'types'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'

interface Data {
  brand_kit: BrandKit
}

export interface Variables {
  name: string
  sources: Array<
    | Pick<PlainText, 'id' | 'type'>
    | Pick<Website, 'id' | 'type'>
    | Pick<ExampleBrand, 'id' | 'type'>
    | { type: 'ExternalIntegration'; id: number }
  >
  signal?: AbortSignal
}

export const createBrandKit = async ({ workspaceId, sources, name, signal }: Variables & { workspaceId: number }) => {
  const res = await sendToApi<Data>(`/w/${workspaceId}/brand_kits`, {
    method: 'POST',
    body: {
      workspace: workspaceId,
      sources,
      name,
    },
    signal,
  })

  if (res.type === 'exception' && res.data instanceof DOMException && res.data.name === 'AbortError') {
    throw res.data
  }

  if (res.type !== SUCCESS_RESPONSE_TYPE) {
    if (res.data === 'cancel') {
      throw new DOMException('The operation was aborted.', 'AbortError')
    }

    throw new Error(res.data)
  }

  return res.data
}

export interface Options extends Omit<UseMutationOptions<BrandKit, Error, Variables>, 'mutationFn'> {}

export const useCreateBrandKit = (options?: Options) => {
  const workspaceId = useCurrentWorkspaceId()
  const queryClient = useQueryClient()

  return useMutation<BrandKit, Error, Variables>(async (vars) => {
    const { sources, name, signal } = vars

    const result = await createBrandKit({
      workspaceId,
      sources,
      name,
      signal,
    })

    queryClient.invalidateQueries(['workspaces', workspaceId, 'brandKits'])

    return result.brand_kit
  }, options)
}
